/**
 * CartResolutionTab — Wave 6.2 RESOLUTION-METRICS-KPI
 *
 * RPM sub-route: surfaces the cart-resolution KPI bundle from
 * /v3/metrics/cart-resolution.  Four panels in a 2×2 grid:
 *
 *   1. (top-left)     Resolution funnel — Origin × Resolution stacked bars
 *   2. (top-right)    Time-to-resolution — overlapping histograms p50 + p95
 *   3. (bottom-left)  Inefficiency hotspots — officers above Cancelled threshold
 *   4. (bottom-right) Compliance signals — AA-rate by branch, gap counts
 *
 * Time-window selector mirrors RPM's filter chips.  Branch + origin filters
 * piggy-back the existing branchOptions from ConfigContext.
 *
 * Polling cadence: 60s (matches SupervisorHome).  Per-tuple cache
 * invalidation is enforced server-side by the service.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, WidgetCard } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { useConfig } from '@/contexts/ConfigContext';
import type {
  CartResolutionMetricsResponse,
  CartResolutionRange,
} from '@retail-ops/shared-types';
import { ResolutionFunnel } from './charts/ResolutionFunnel';
import { TimeToResolutionHistogram } from './charts/TimeToResolutionHistogram';
import { InefficiencyHotspots } from './charts/InefficiencyHotspots';
import { ComplianceSignals } from './charts/ComplianceSignals';
import { CommitPipelineTiming } from './charts/CommitPipelineTiming';

const POLL_INTERVAL_MS = 60_000;

const RANGE_LABELS: Record<CartResolutionRange, string> = {
  '24h': 'Last 24h',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  ytd: 'Year-to-date',
};

const ORIGIN_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'all', label: 'All origins' },
  { value: 'fsr-self', label: 'FSR self-serve' },
  { value: 'lobby-queue', label: 'Lobby queue' },
  { value: 'lobby-entry-walkin', label: 'Lobby — walk-in' },
  { value: 'lobby-entry-appointment', label: 'Lobby — appt' },
  { value: 'kiosk', label: 'Kiosk' },
  { value: 'rcp-portal', label: 'Customer portal' },
];

export function CartResolutionTab(): JSX.Element {
  const client = useApiClient();
  const { user } = useAuth();
  const { config } = useConfig();

  const isSupervisorPlus =
    user?.primaryRole === 'Supervisor' ||
    user?.primaryRole === 'Manager' ||
    user?.primaryRole === 'Executive' ||
    user?.primaryRole === 'SuperAdmin';

  const [range, setRange] = useState<CartResolutionRange>('7d');
  const [branchFilter, setBranchFilter] = useState<string>('all');
  const [originFilter, setOriginFilter] = useState<string>('all');
  const [data, setData] = useState<CartResolutionMetricsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(async () => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError(null);
    try {
      const opts: { branch?: string; origin?: string; signal: AbortSignal } = {
        signal: ctrl.signal,
      };
      if (branchFilter !== 'all') opts.branch = branchFilter;
      if (originFilter !== 'all') opts.origin = originFilter;
      const res = await client.retail.metrics.cartResolution(range, opts);
      if (res.success && res.data) {
        setData(res);
        setLastRefreshed(new Date());
      } else {
        setError(res.error ?? 'Metrics request returned no data.');
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Metrics request failed.');
    } finally {
      setLoading(false);
    }
  }, [client, range, branchFilter, originFilter]);

  useEffect(() => {
    if (!isSupervisorPlus) return;
    void fetchData();
    const id = window.setInterval(() => {
      void fetchData();
    }, POLL_INTERVAL_MS);
    return () => {
      window.clearInterval(id);
      abortRef.current?.abort();
    };
  }, [fetchData, isSupervisorPlus]);

  const branchOptions = useMemo(
    () => [
      { value: 'all', label: 'All branches' },
      ...config.branches.map((b) => ({ value: b.branchCode, label: b.branchName })),
    ],
    [config.branches],
  );

  if (!isSupervisorPlus) {
    return (
      <div className="module-page">
        <header className="module-page__header">
          <h1>Cart resolution metrics</h1>
          <p className="module-landing__scope">
            This surface is role-gated to Supervisor / Manager / Executive.
          </p>
        </header>
      </div>
    );
  }

  const body = data?.data;

  return (
    <div className="module-page">
      <div className="rpm-header">
        <div className="rpm-header__top">
          <div>
            <h1>Cart resolution</h1>
            <div className="sub">
              KPIs and inefficiency signals from the resolution-code dataset · poll 60s
            </div>
          </div>
          <div className="rpm-header__actions">
            <span className="rpm-refresh-stamp">
              {lastRefreshed
                ? `Last refreshed ${relative(lastRefreshed)}`
                : loading
                ? 'Loading…'
                : '—'}
            </span>
            <Button variant="ghost" size="sm" onClick={() => void fetchData()}>
              ⟳ Refresh
            </Button>
          </div>
        </div>

        <div className="rpm-filter-chips" role="tablist" aria-label="Cart-resolution filters">
          <span className="rpm-chip-label">Range:</span>
          {(Object.keys(RANGE_LABELS) as CartResolutionRange[]).map((r) => (
            <button
              key={r}
              type="button"
              role="tab"
              aria-selected={range === r}
              className={`rpm-chip ${range === r ? 'is-active' : ''}`.trim()}
              onClick={() => setRange(r)}
            >
              {RANGE_LABELS[r]}
            </button>
          ))}
          <select
            className="rpm-chip"
            value={branchFilter}
            onChange={(e) => setBranchFilter(e.target.value)}
            aria-label="Branch filter"
          >
            {branchOptions.map((b) => (
              <option key={b.value} value={b.value}>
                {b.label}
              </option>
            ))}
          </select>
          <select
            className="rpm-chip"
            value={originFilter}
            onChange={(e) => setOriginFilter(e.target.value)}
            aria-label="Origin filter"
          >
            {ORIGIN_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="rpm-alert-banner" role="alert" style={{ marginBottom: 'var(--sp-6)' }}>
          <span>
            <strong>● Metrics error:</strong> {error}
          </span>
        </div>
      )}

      {body ? (
        <div
          className="rpm-resolution-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 'var(--sp-7)',
          }}
        >
          <WidgetCard
            title="Resolution funnel"
            stripe="pipeline"
            count={body.counts.total}
            footer={<span>Origin × Resolution · click a segment to drill</span>}
          >
            <ResolutionFunnel
              range={range}
              rows={body.byOrigin}
              branchFilter={branchFilter}
            />
          </WidgetCard>

          <WidgetCard
            title="Time-to-resolution"
            stripe="sla"
            count={`${body.timeToResolutionMinutes.processed.count + body.timeToResolutionMinutes.withdrawn.count + body.timeToResolutionMinutes.adverseAction.count + body.timeToResolutionMinutes.cancelled.count} resolved`}
            footer={<span>Bucketed histograms · p50 + p95 markers</span>}
          >
            <TimeToResolutionHistogram
              range={range}
              bundle={body.timeToResolutionMinutes}
              branchFilter={branchFilter}
            />
          </WidgetCard>

          <WidgetCard
            title="Inefficiency hotspots"
            stripe="overrides"
            count={`${body.inefficiencySignals.cancelledRatePerOfficer.length} flagged`}
            footer={<span>Threshold from SystemSettings · click row to drill</span>}
          >
            <InefficiencyHotspots
              range={range}
              officers={body.byOfficer}
              signals={body.inefficiencySignals}
              thresholds={body.thresholds}
              branchFilter={branchFilter}
            />
          </WidgetCard>

          <WidgetCard
            title="Compliance signals"
            stripe="compliance"
            count={`${body.counts.adverseAction} AA · ${body.artifactSignals.processedZeroArtifactRows} gaps`}
            footer={<span>Adverse-Action by branch · processed-zero-artifact · lobby preconditions</span>}
          >
            <ComplianceSignals
              range={range}
              artifactSignals={body.artifactSignals}
              inefficiencySignals={body.inefficiencySignals}
              thresholds={body.thresholds}
            />
          </WidgetCard>

          {/* Wave 6.3 — System-Error analysis panel. */}
          {body.byErrorCode && body.byErrorCode.length > 0 && (
            <WidgetCard
              title="System-Error analysis"
              stripe="overrides"
              count={`${body.counts.systemError ?? 0} system-error closures`}
              footer={<span>Top error codes by count · click to drill</span>}
            >
              <SystemErrorAnalysisPanel rows={body.byErrorCode} />
            </WidgetCard>
          )}

          {/* Wave 6.3 — mixed-disposition panel (e.g. 2 of 3 accounts succeeded). */}
          {body.mixedDispositionCarts && body.mixedDispositionCarts.length > 0 && (
            <WidgetCard
              title="Per-account disposition"
              stripe="pipeline"
              count={`${body.mixedDispositionCarts.length} mixed-outcome carts`}
              footer={<span>Carts where some products succeeded but others hit System-Error</span>}
            >
              <MixedDispositionPanel rows={body.mixedDispositionCarts} />
            </WidgetCard>
          )}

          {/* Wave 7 OBSERVABILITY — commit-pipeline timing breakdown. */}
          <WidgetCard
            title="Commit pipeline timing"
            stripe="sla"
            count="per-step latency"
            footer={
              <span>
                p50 / p95 by step · top-failing-step bar chart · click to drill into failed carts
              </span>
            }
          >
            <CommitPipelineTiming range={range} />
          </WidgetCard>
        </div>
      ) : (
        <div style={{ padding: 'var(--sp-9)', fontSize: 13, color: 'var(--ink-600)' }}>
          {loading ? 'Loading cart-resolution metrics…' : 'No data yet.'}
        </div>
      )}
    </div>
  );
}

function relative(d: Date): string {
  const ms = Date.now() - d.getTime();
  if (ms < 5_000) return 'just now';
  if (ms < 60_000) return `${Math.floor(ms / 1000)}s ago`;
  return `${Math.floor(ms / 60_000)}m ago`;
}

// ---------------------------------------------------------------------------
// Wave 6.3 — System-Error analysis panel
// ---------------------------------------------------------------------------

interface SystemErrorAnalysisPanelProps {
  rows: ReadonlyArray<{
    errorCode: string;
    errorLabel?: string;
    count: number;
    avgRecoveryMinutes?: number;
    topBranches?: ReadonlyArray<{ branchCode: string; branchName: string; count: number }>;
    topOfficers?: ReadonlyArray<{ officerCode: string; displayName: string; count: number }>;
  }>;
}

function SystemErrorAnalysisPanel({ rows }: SystemErrorAnalysisPanelProps): JSX.Element {
  const sorted = [...rows].sort((a, b) => b.count - a.count);
  const total = sorted.reduce((acc, r) => acc + r.count, 0);
  return (
    <div style={{ padding: 'var(--sp-4)', fontSize: 13 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ textAlign: 'left', color: 'var(--ink-600)', fontSize: 11 }}>
            <th style={{ padding: '6px 8px' }}>Error</th>
            <th style={{ padding: '6px 8px' }}>Count</th>
            <th style={{ padding: '6px 8px' }}>% of SE</th>
            <th style={{ padding: '6px 8px' }}>Avg recovery</th>
            <th style={{ padding: '6px 8px' }}>Top branch</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => (
            <tr key={r.errorCode} style={{ borderTop: '1px solid var(--surface-border)' }}>
              <td style={{ padding: '6px 8px' }}>
                <strong>{r.errorLabel ?? r.errorCode}</strong>
                <div style={{ fontSize: 11, color: 'var(--ink-500)' }}>{r.errorCode}</div>
              </td>
              <td style={{ padding: '6px 8px' }}>{r.count}</td>
              <td style={{ padding: '6px 8px' }}>{total ? `${Math.round((r.count / total) * 100)}%` : '—'}</td>
              <td style={{ padding: '6px 8px' }}>
                {r.avgRecoveryMinutes != null ? `${Math.round(r.avgRecoveryMinutes)}m` : '—'}
              </td>
              <td style={{ padding: '6px 8px' }}>
                {r.topBranches && r.topBranches[0]
                  ? `${r.topBranches[0].branchName} (${r.topBranches[0].count})`
                  : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Wave 6.3 — Mixed-disposition panel (per-account divergence)
// ---------------------------------------------------------------------------

interface MixedDispositionPanelProps {
  rows: ReadonlyArray<{
    cartId: string;
    displayLabel: string;
    customerName?: string | null;
    branchName?: string;
    branch: string;
    totalProducts: number;
    processedCount: number;
    systemErrorCount: number;
    errorCodes: ReadonlyArray<string>;
  }>;
}

function MixedDispositionPanel({ rows }: MixedDispositionPanelProps): JSX.Element {
  return (
    <div style={{ padding: 'var(--sp-4)', fontSize: 13 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ textAlign: 'left', color: 'var(--ink-600)', fontSize: 11 }}>
            <th style={{ padding: '6px 8px' }}>Cart</th>
            <th style={{ padding: '6px 8px' }}>Customer</th>
            <th style={{ padding: '6px 8px' }}>Branch</th>
            <th style={{ padding: '6px 8px' }}>Products</th>
            <th style={{ padding: '6px 8px' }}>Errors</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.cartId} style={{ borderTop: '1px solid var(--surface-border)' }}>
              <td style={{ padding: '6px 8px' }}>{r.displayLabel}</td>
              <td style={{ padding: '6px 8px' }}>{r.customerName ?? '—'}</td>
              <td style={{ padding: '6px 8px' }}>{r.branchName ?? r.branch}</td>
              <td style={{ padding: '6px 8px' }}>
                {r.processedCount} of {r.totalProducts} succeeded
                <div style={{ fontSize: 11, color: 'var(--pcb-red)' }}>
                  {r.systemErrorCount} system-error
                </div>
              </td>
              <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: 11 }}>
                {r.errorCodes.join(', ')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
